import { ClickAwayListener, Popper } from "@mui/base";
import type { Placement } from "@popperjs/core";
import clsx from "clsx";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "~/contexts/AuthProvider";
import { FEATURE_FLAGS, useFeatureFlag } from "~/featureFlags";
import { getUserFullName } from "~/helpers/user";
import { useImpersonation } from "~/hooks/useImpersonation";
import { routes } from "~/router";

import { Icon } from "../../assets/icons/Icon";
import style from "./DashboardSidebar.module.scss";

type Props = {
  isSidebarOpen: boolean;
  label: string;
  withSettings?: boolean;
  popoverPlacement?: Placement;
};

export const SidebarUserMenu: React.FC<Props> = ({
  isSidebarOpen,
  label,
  withSettings = true,
  popoverPlacement = "right-start",
}) => {
  const navigate = useNavigate();

  const { user, clearAuth } = useAuth();
  const currentUserName = getUserFullName(user);
  const { canReturnToAccount, returnToAccount, clearImpersonationState } =
    useImpersonation();

  const settingsEnabled =
    useFeatureFlag(FEATURE_FLAGS.SETTINGS) && withSettings;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const names = currentUserName.split(" ").filter(Boolean);
  const initials =
    names.length > 1 ? `${names[0][0]}${names[1][0]}` : (names[0]?.[0] ?? "?");

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    clearImpersonationState();
    clearAuth();
    window.Intercom?.("shutdown");
    navigate(routes.auth.login());
  };

  return (
    <>
      <div className={clsx(style.menuLink)} onClick={handleOpen}>
        <span className={style.menuLinkIconTitle}>
          <span className={style.menuIconTitle}>
            <span className={style.userAvatar}>{initials.toUpperCase()}</span>
            {isSidebarOpen && (
              <span className={clsx(style.label, style.labelTruncate)}>
                {label}
              </span>
            )}
          </span>
        </span>
      </div>

      <Popper open={open} anchorEl={anchorEl} placement={popoverPlacement}>
        <ClickAwayListener onClickAway={handleClose}>
          <div className={style.popperWrapper}>
            <div className={style.userMenuDropdown}>
              <div className={style.userMenuHeader}>
                {/* @ts-expect-error TS2339 non-existing className*/}
                <span className={style.userMenuAvatar}>{initials}</span>
                <span className={style.userMenuName}>{currentUserName}</span>
                <span className={style.userMenuEmail}>{user?.email}</span>
              </div>

              {settingsEnabled && (
                <div
                  className={style.userMenuOption}
                  onClick={() => {
                    handleClose();
                    navigate(routes.settings.root());
                  }}
                >
                  <Icon iconName="SettingIcon" width="1rem" height="1rem" />
                  <span>Settings</span>
                </div>
              )}

              <div className={style.userMenuDivider} />

              {canReturnToAccount && (
                <div
                  className={style.userMenuOption}
                  onClick={() => {
                    handleClose();
                    returnToAccount();
                  }}
                >
                  <Icon iconName="LogoutIcon" width="1rem" height="1rem" />
                  <span>Exit</span>
                </div>
              )}

              <div
                className={style.userMenuOption}
                onClick={() => {
                  handleClose();
                  handleLogout();
                }}
              >
                <Icon iconName="LogoutIcon" width="1rem" height="1rem" />
                <span>Log out</span>
              </div>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
